import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

// Real OTP: code is sent by the backend (email or SMS) and checked server-side; returns a verification token.
export const OtpVerify = ({ channel, target, verified, onVerified, testid }) => {
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(wait - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  useEffect(() => {
    setSent(false);
    setCode("");
  }, [target]);

  const send = async () => {
    setBusy(true);
    try {
      const r = await api.post("/otp/send", { channel, target });
      setSent(true);
      setWait(r.data.resend_after);
      if (channel === "email") toast.success("Code sent to your email");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      const r = await api.post("/otp/verify", { channel, target, code });
      onVerified(r.data.verification_token);
      if (channel === "email") toast.success("Verified");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (verified)
    return (
      <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-700" data-testid={`${testid}-verified`}>
        <CheckCircle2 className="h-4 w-4" /> Verified
      </p>
    );

  return (
    <div className="flex flex-wrap items-center gap-2">
      {sent && (
        <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" className="h-10 w-36" data-testid={`${testid}-code-input`} />
      )}
      {sent && (
        <Button type="button" size="sm" className="h-10 rounded-full" disabled={busy || code.length < 4} onClick={verify} data-testid={`${testid}-verify-button`}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
        </Button>
      )}
      <Button type="button" variant="outline" size="sm" className="h-10 rounded-full" disabled={busy || wait > 0 || !target} onClick={send} data-testid={`${testid}-send-button`}>
        {wait > 0 ? `Resend in ${wait}s` : sent ? "Resend code" : channel === "email" ? "Send email code" : "Send OTP"}
      </Button>
    </div>
  );
};
